import { DischargePatient, DischargeNinetyDaySummary, DischargeClinicalEvent } from '~/types/discharge';

// Deterministic per-patient hash so demo data is stable across re-renders.
function hashOf(id: string): number {
  return Array.from(id).reduce((acc, c) => acc + c.charCodeAt(0), 0);
}

function daysAgoIso(days: number): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - days);
  return d.toISOString();
}

export function buildNinetyDaySummary(patient: DischargePatient): DischargeNinetyDaySummary {
  const h = hashOf(patient.id);
  // Explicit values (from a real profile) win over the generated defaults;
  // anything omitted falls back to the deterministic demo generation. `trend`
  // is resolved first so the derived description and narrative follow it.
  const override = patient.ninetyDaySummary;

  const recentAdmissions = override?.recentAdmissions ?? ((h % 3) + 1);   // 1-3
  const erVisits = override?.erVisits ?? (h % 4);                        // 0-3
  const totalInpatientDays = override?.totalInpatientDays ?? (patient.currentLOS + 4 + (h % 8));

  const trendKey = h % 3;
  const trend: DischargeNinetyDaySummary['trend'] =
    override?.trend ?? (trendKey === 0 ? 'improving' : trendKey === 1 ? 'stable' : 'declining');

  const trendDescription = override?.trendDescription ?? (
    trend === 'improving' ? 'Functional status has improved relative to admission baseline; vitals trending toward stability.'
    : trend === 'stable' ? 'Clinical status has been broadly stable over the 90-day window with manageable comorbidities.'
    : 'Repeated utilization and declining functional metrics suggest a downward trajectory.'
  );

  const events: DischargeClinicalEvent[] = override?.events ?? [
    {
      date: daysAgoIso(patient.currentLOS),
      type: 'admission',
      description: `Admitted to ${patient.facilityName} for ${patient.primaryDiagnosis.toLowerCase()}.`,
      location: patient.facilityName,
    },
    {
      date: daysAgoIso(patient.currentLOS + 12 + (h % 6)),
      type: 'er_visit',
      description: 'Brief ED presentation for symptom flare; discharged same day.',
      location: patient.facilityName,
    },
    {
      date: daysAgoIso(patient.currentLOS + 28 + (h % 9)),
      type: 'medication_change',
      description: 'Medication regimen adjusted based on response to therapy.',
    },
    {
      date: daysAgoIso(patient.currentLOS + 42 + (h % 8)),
      type: 'procedure',
      description: 'Outpatient procedure performed; tolerated well.',
      location: patient.facilityName,
    },
    {
      date: daysAgoIso(patient.currentLOS + 56 + (h % 12)),
      type: 'discharge',
      description: 'Discharged from the prior inpatient stay with home-based follow-up arranged.',
      location: patient.facilityName,
      destination: h % 2 === 0 ? 'Home' : 'Home + Home Health',
    },
    {
      date: daysAgoIso(patient.currentLOS + 60 + (h % 12)),
      type: 'admission',
      description: `Prior inpatient admission for related ${patient.primaryDiagnosis.toLowerCase()} workup.`,
      location: patient.facilityName,
    },
  ];

  const keyFindings = override?.keyFindings ?? [
    `Primary diagnosis: ${patient.primaryDiagnosis}.`,
    `${recentAdmissions} hospital admission${recentAdmissions === 1 ? '' : 's'} and ${erVisits} ED visit${erVisits === 1 ? '' : 's'} in the past 90 days.`,
    'Insurance authorization status reviewed; no current barriers to post-acute placement.',
    'Caregiver engagement confirmed with primary contact.',
  ];

  const narrativeSummary = override?.narrativeSummary ?? (
    `${patient.name} is a ${patient.age}-year-old with ${patient.primaryDiagnosis.toLowerCase()}[[1]] ` +
    `admitted ${patient.currentLOS} day${patient.currentLOS === 1 ? '' : 's'} ago to ${patient.facilityName}. ` +
    `Across the prior 90 days the patient has had ${recentAdmissions} inpatient admission${recentAdmissions === 1 ? '' : 's'} ` +
    `and ${erVisits} ED visit${erVisits === 1 ? '' : 's'}, with ${totalInpatientDays} total inpatient days[[2]]. ` +
    `\n\n${trendDescription}[[3]]`
  );

  // Generated citations backing the templated narrative's [[n]] markers. Explicit
  // per-patient references (when supplied) win; otherwise unused generated refs
  // simply never render because an override narrative carries no markers.
  const references = override?.references ?? [
    {
      id: 1,
      source: 'Admission Note',
      sourceType: 'note' as const,
      detail: `${patient.attendingPhysician} · on admission`,
      snippet: `Admitted for ${patient.primaryDiagnosis.toLowerCase()}`,
    },
    {
      id: 2,
      source: 'Encounter / Utilization History',
      sourceType: 'claims' as const,
      detail: 'Utilization summary · last 90 days',
      snippet: `${recentAdmissions} admission${recentAdmissions === 1 ? '' : 's'}, ${erVisits} ED visit${erVisits === 1 ? '' : 's'}, ${totalInpatientDays} inpatient days`,
    },
    {
      id: 3,
      source: 'Care Team Assessment',
      sourceType: 'assessment' as const,
      detail: 'Case Management · recent',
      snippet: trendDescription,
    },
  ];

  return {
    narrativeSummary,
    references,
    recentAdmissions,
    erVisits,
    totalInpatientDays,
    currentLOS: override?.currentLOS ?? patient.currentLOS,
    events,
    trend,
    trendDescription,
    keyFindings,
    lastUpdated: override?.lastUpdated ?? daysAgoIso(0),
  };
}
